import { useMemo, createElement } from "react";
import React from "react";
import { createPortal } from "react-dom";
import { mapAttributesAsReactProps } from "./HTMLNode.js";
import mergeRefs from "react-merge-refs";
let id = 0;
/**
 * Find all Smartfire components to portal on the current level.
 * If you have a node like this:
 * ```html
 * <div>
 *  <div>
 *    <smartfire-component id="1"/>
 *  </div>
 *  <smartfire-component id="2">
 *   <smartfire-component id="3"/>
 *  </smartfire-component>
 * </div>
 * ```
 * The function will return [smartfire-component-1, smartfire-component-2] but not smartfire-component-3 as it will be rendered by smartfire-component-2
 *
 * Warning, this function will modify the DOM by replacing the smartfire-component node with a div node to allow React to render the component.
 * This means this function should only be called once per parent node.
 *
 * @param node Node to search for Smartfire components, it can be any node type.
 * @returns List of Smartfire Block Nodes to portal
 */
export function findSmartfireComponentToPortal(node) {
    var _a;
    const nodesToPortal = [];
    const children = node.children;
    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.isDOMNode) {
            // Search for nested smartfire components
            const nestedSmartfireComponent = findSmartfireComponentToPortal(child);
            if (nestedSmartfireComponent) {
                nodesToPortal.push(...nestedSmartfireComponent);
            }
        }
        else if (child.isHTMLBoundaryNode) {
            // We do not render this element as a portal to avoid react nesting
            // Instead, we just search for nested smartfire components
            nodesToPortal.push(...findSmartfireComponentToPortal(child));
        }
        else if (child.isSmartfireComponentNode) {
            // If we already have a rendering node, we don't need to do anything
            // And just add it to the list of nodes to portal
            if (child.renderingNode) {
                nodesToPortal.push(child);
                continue;
            }
            // Otherwise, we need to create a new rendering node
            const domElement = child.domNode;
            // Remove all children from previous node
            domElement.innerHTML = "";
            // Create a new node
            const newNode = document.createElement("div");
            // Add class and id to help target the node from CSS and JS
            newNode.classList.add("smartfire-component-portal");
            newNode.id = `smartfire-component-${id++}`;
            // Replace the old node with the new node
            (_a = domElement.parentElement) === null || _a === void 0 ? void 0 : _a.replaceChild(newNode, domElement);
            // Set the rendering node
            child.renderingNode = newNode;
            nodesToPortal.push(child);
        }
        else {
            // Node can be discarded (text node, comment node, etc.)
        }
    }
    return nodesToPortal;
}
/**
 * Hook to render children of a node as original HTML elements.
 * It will create a ref that should be added to any HTML element allowing children.
 * On Layout effect, it will append all children to the parent.
 * It will also return a list of Smartfire Block Nodes to portal from the current node.
 * @param node Node to render in the ref
 * @param options Options to affect the return.
 * @returns Reference to bound and list of Smartfire Block Nodes to portal
 */
export function useHTMLBoundary(node, options) {
    const portalNodes = useMemo(() => (options === null || options === void 0 ? void 0 : options.disableNestedRendering) === true ? [] : findSmartfireComponentToPortal(node), [node, options === null || options === void 0 ? void 0 : options.disableNestedRendering]);
    const createdRef = React.useRef(null);
    const ref = mergeRefs([
        createdRef,
        ...((options === null || options === void 0 ? void 0 : options.ref) ? [options.ref] : [])
    ]);
    React.useLayoutEffect(() => {
        var _a;
        if (createdRef.current) {
            const parent = createdRef.current;
            // Cleanup all children
            parent.innerHTML = "";
            // Append all nodes to the parent
            const nodes = node.children;
            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i];
                parent.appendChild(
                // If the node has a rendering node, we use it instead of the dom node
                (_a = node.renderingNode) !== null && _a !== void 0 ? _a : node.domNode);
            }
        }
    }, [node]);
    return { ref, portalNodes };
}
/**
 * Renders a node that contains HTML elements.
 * It will create a new div and append all children to it.
 * For nested Smartfire Blocks, we will create React.Portal to render them.
 * Only firsts encountered Smartfire Block will be rendered as a React.Portal.
 */
export const HTMLBoundaryNode = (props) => {
    const node = props.node;
    const { ref, portalNodes } = useHTMLBoundary(node, { disableNestedRendering: props.disableNestedRendering });
    // Try to detect the tag name of the node
    let tag = node.domNode instanceof HTMLElement
        ? node.domNode.tagName.toLowerCase()
        : "div";
    // If the tag is a smartfire-html-boundary, we use a div instead
    if (tag.startsWith("smartfire-html-boundary")) {
        tag = "div";
    }
    // Remap attributes of the original node to React props
    const attributes = useMemo(() => mapAttributesAsReactProps(node.domNode), [node.domNode]);
    const className = useMemo(() => {
        if (!attributes.className) {
            return "smartfire-html-boundary";
        }
        if (attributes.className.includes("smartfire-html-boundary")) {
            return attributes.className;
        }
        return `${attributes.className} smartfire-html-boundary`;
    }, [attributes.className]);
    return createElement(tag, Object.assign(Object.assign({ ref }, attributes), { className }), portalNodes.map((node, i) => {
        var _a;
        return createPortal(node.asReactElement(), (_a = node.renderingNode) !== null && _a !== void 0 ? _a : node.domNode, i.toString());
    }));
};

//# sourceMappingURL=HTMLBoundaryNode.js.map
